import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import emailjs from '@emailjs/browser';
import { GREEN_COLOR } from '../constants';
import { Section, SectionContent, SectionTitle, SectionSubtitle, SectionDescription } from '../styles/sections';
import { CtaButton } from '../styles/hero';
import {
  SignupContainer,
  SignupImage,
  SignupForm,
  InputWrapper,
  Input,
  Label,
  ErrorMessage,
} from '../styles/signup';

function Signup() {
  const [formData, setFormData] = useState({ name: '', email: '', teamName: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateField = (name, value) => {
    if (name === 'name')
      return !value.trim() ? 'Name is required' : value.trim().length < 2 ? 'Name too short' : '';
    if (name === 'email')
      return !value ? 'Email is required' : !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email' : '';
    if (name === 'teamName')
      return !value.trim()
        ? 'Team name is required'
        : value.trim().length < 2
        ? 'Team name too short'
        : '';
    return '';
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      teamName: validateField('teamName', formData.teamName),
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.email && !newErrors.teamName;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors');
      return;
    }

    setIsLoading(true);

    const templateParams = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      teamName: formData.teamName.trim(),
      timestamp: new Date().toLocaleString(),
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_USER_ID
      )
      .then(() => {
        toast.success('You\u2019re in! We\u2019ll email you soon');
        setFormData({ name: '', email: '', teamName: '' });
        setErrors({});
        setIsSubmitted(true);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('EmailJS Error:', err);
        toast.error('Failed to send. Try again or email us directly.');
        setIsLoading(false);
      });
  };

  return (
    <Section id="signup" backgroundColor="#fff">
      <SectionContent>
        <SectionSubtitle style={{ color: '#000' }}>Join the Team</SectionSubtitle>
        <SectionTitle style={{ textAlign: 'left' }}>Get Started Now</SectionTitle>
        <SectionDescription style={{ textAlign: 'left' }}>
          Sign up now to join our newsletter and get regular updates.
        </SectionDescription>

        <SignupContainer>
          <SignupImage src="/bottom-pic.jpg" alt="Team celebrating" loading="lazy" />

          {!isSubmitted ? (
            <SignupForm onSubmit={handleSubmit}>
              <InputWrapper>
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  disabled={isLoading}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </InputWrapper>

              <InputWrapper>
                <Label htmlFor="email">Your Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  disabled={isLoading}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </InputWrapper>

              <InputWrapper>
                <Label htmlFor="teamName">Team Name</Label>
                <Input
                  id="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  error={!!errors.teamName}
                  disabled={isLoading}
                />
                {errors.teamName && <ErrorMessage>{errors.teamName}</ErrorMessage>}
              </InputWrapper>

              <CtaButton type="submit" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Subscribe To Our Newsletter'}
              </CtaButton>
            </SignupForm>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ fontSize: '1.6rem', fontWeight: '700', color: GREEN_COLOR }}
            >
              Thank you, {formData.name}! You\u2019re on the list — we\u2019ll email you soon!
            </motion.div>
          )}
        </SignupContainer>
      </SectionContent>
    </Section>
  );
}

export default Signup;
